
import { Subject, Observable } from "rxjs";
import { EnvDir } from "./com/comEnvironmentFileSystem";
import Utils from "../shared/utils";

import { ModelManager } from "../shared/modelManager";

// models to loading from Server
import { DataModel, SyncModel, SyncDataModel } from "../shared/models/syncModel";
import * as models from "../shared/models";
import * as sotmodels from "../../sot/models";
import { Mutex } from "../forked/ts-mutex";
import { CircularInjectionHelper } from "./circularInjectionHelper";
import { ContentType, GetContentDirFromType, ContentTypeGroups, UserContentTypes, YourAPPProductType } from "../shared/contentTypes";
import { IObjectObserver, IObjectObserverData } from "./IObjectObserver";
import { Moment } from "moment";
import { ModelManagerProduct } from "../shared/modelManagerProduct";
import { environment } from "../../../environments/environment";
import { ModelManagerWebshopLanguages } from "../shared/modelManagerWebshopLanguages";
/** ObjectObserver Service for manage the data in storage and over internet */

/** intern helper class */
class ServerCallHelper {
  public RequestData: SyncModel = null;
  public FunctionName: string = null;
  public SyncDataModelConstructor: new () => SyncDataModel = null;
}

export abstract class ObjectObserverData implements IObjectObserverData {
  public abstract GetKey(): string;
  public abstract intern_data: { [key: string]: any };
}

/** Ermöglicht ein gezieltes abrufen von Artikeln */
export class ProductObjectObserverData extends ObjectObserverData {
  public constructor(hyp_cus_nr: number,
    sortiment: string) {
    super();
    const me = this;
    me.intern_data = {
      hyp_cus_nr: hyp_cus_nr,
      sortiment: sortiment.toUpperCase()
    }
  }

  public GetKey(): string {
    return this.intern_data["hyp_cus_nr"] + "_" + this.intern_data["sortiment"];
  }
  public intern_data: { [key: string]: any; };
}

/** Ermöglicht ein gezieltes abrufen von Spracheinstellungen */
export class WebshopLanguagesObjectObserverData extends ObjectObserverData {
  /**
   * 
   * @param hyp_cus_nr Standort (-1 = Zentrale)
   * @param language_code 'en', 'fr'
   */
  public constructor(hyp_cus_nr: number,
    language_code: string) {
    super();
    const me = this;
    me.intern_data = {
      location: hyp_cus_nr,
      code: language_code.toLowerCase()
    }
  }

  public GetKey(): string {
    return this.intern_data["location"] + "_" + this.intern_data["code"];
  }
  public intern_data: { [key: string]: any; };
}

/** Ermöglicht das gezielte Abrufen von SOT Daten */
export class SOTObjectObserverData extends ObjectObserverData {
  public constructor(hyp_cus_nr: number) {
    super();
    const me = this;
    me.intern_data = {
      hyp_cus_nr: hyp_cus_nr
    }
  }

  public GetKey(): string {
    return this.intern_data["hyp_cus_nr"];
  }
  public intern_data: { [key: string]: any; };
}

export class ObjectObserver<T extends DataModel> implements IObjectObserver<T> {

  private _SafeGetManagerMutex = new Mutex();
  private _needSave: boolean = false;
  private _manager: ModelManager<T> = null;
  private _observerChanged: Subject<(number | string)[]> = new Subject(); // todo try change it to send changed keys as number[] | srting[]

  /** UAPP CoreLogService for Logging */
  constructor(private _contentType: ContentType,
    private _data: ObjectObserverData,
    private _helper: CircularInjectionHelper) {
  }

  /** ist speichern notwendig */
  private needSave(): boolean {
    return this._needSave || this._manager.needSave();
  }

  /** manager wurde erfolgreich gespeichert */
  private markSaved() {
    this._needSave = false;
    this._manager.markSaved(712468);
  }

  /** server synchronisation is for persitent data. YourAppServer, needs for synchronisation with the YourAPPServer */
  public get SynchronizingWithServer(): boolean {
    const me = this;
    // server synchronisation is for persitent data
    let allow = me._contentType !== ContentType.Local_ValueStorage
    // user's contentTypes, can only synchronize if, user is not nobody
    if (allow && UserContentTypes.includes(me._contentType)) {
      allow = !me._helper.yourAppServer.Auth.IsNobody;
    }
    return allow;
  }

  /** Returnt den ContentType */
  public get ContentType(): ContentType {
    return this._contentType;
  }

  /** Returnt die IObjectObserverData */
  public get ObserverData(): IObjectObserverData {
    return this._data;
  }

  /** Ermitling des Speicherverzeichnisses anhand vom ContentType */
  public get StorageName(): string {
    const me = this;
    const storage = GetContentDirFromType(me._contentType);
    let storageName = (storage ? storage.toString() : null);
    if (storageName == null) {
      me._helper.coreLogService.error("ObjectObserver.StorageName", "ContentType unknown", me._contentType);
    } else if (!Utils.IsNull(me._data)) {
      storageName += "_" + me._data.GetKey();
    }
    return storageName;
  }

  private CreateNewManager(): ModelManager<T> {
    const me = this;
    let newManager = null;
    switch (me._contentType) {
      case ContentType.Location:
        newManager = new ModelManager(models.LocationDataModel, me._helper.coreLogService);
        break;
      case ContentType.News:
        newManager = new ModelManager(models.NewsDataModel, me._helper.coreLogService);
        break;
      case ContentType.Coupon:
        newManager = new ModelManager(models.CouponDataModel, me._helper.coreLogService);
        break;
      case ContentType.Event:
        newManager = new ModelManager(models.EventDataModel, me._helper.coreLogService);
        break;
      case ContentType.Impressum:
        newManager = new ModelManager(models.ImpressumDataModel, me._helper.coreLogService);
        break;
      case ContentType.Term:
        newManager = new ModelManager(models.TermDataModel, me._helper.coreLogService);
        break;
      case ContentType.Home_Slider:
        newManager = new ModelManager(models.HomeSliderDataModel, me._helper.coreLogService);
        break;
      case ContentType.Home_Tile:
        newManager = new ModelManager(models.HomeTileDataModel, me._helper.coreLogService);
        break;
      case ContentType.Home_PrioTile:
        newManager = new ModelManager(models.HomePrioTileDataModel, me._helper.coreLogService);
        break;
      case ContentType.Settings:
        newManager = new ModelManager(models.SettingsDataModel, me._helper.coreLogService);
        break;
      case ContentType.Navigation:
        newManager = new ModelManager(models.NavigationDataModel, me._helper.coreLogService);
        break;
      case ContentType.Item_Group:
        newManager = new ModelManager(models.ItemGroupDataModel, me._helper.coreLogService);
        break;
      case ContentType.BonusactionSettings:
        newManager = new ModelManager(models.BonusactionSettingsDataModel, me._helper.coreLogService);
        break;
      case ContentType.Bonusaction:
        newManager = new ModelManager(models.BonusactionDataModel, me._helper.coreLogService);
        break;
      case ContentType.BonusactionBenefit:
        newManager = new ModelManager(models.BonusactionBenefitDataModel, me._helper.coreLogService);
        break;
      case ContentType.MenuSettings:
        newManager = new ModelManager(models.MenuSettingsDataModel2, me._helper.coreLogService);
        break;
      case ContentType.WebshopSettings:
        newManager = new ModelManager(models.WebshopSettingsDataModel2, me._helper.coreLogService);
        break;
      case ContentType.WebshopColorTokens:
        newManager = new ModelManager(models.WebshopColorTokensDataModel, me._helper.coreLogService);
        break;
      case ContentType.ReservationSettings:
        newManager = new ModelManager(models.ReservationSettingsDataModel, me._helper.coreLogService);
        break;
      case ContentType.sub_Product:
        // Product hat ein erweiterten ModelManager, denn er speichert noch die Information, ob ProduktDetails geladen werden sollen
        newManager = new ModelManagerProduct(me._helper.coreLogService);
        break;
      case ContentType.sub_WebshopLanguages:
        // WebshopLanguages hat ein erweiterten ModelManager, denn er speichert noch die Information, ob je Standort und Sprache
        newManager = new ModelManagerWebshopLanguages(me._helper.coreLogService);
        break;
      case ContentType.ArticleAvailabililty:
        newManager = new ModelManager(models.ArticleAvailabilityDataModel, me._helper.coreLogService);
        break;
      case ContentType.BlackWhiteList:
        newManager = new ModelManager(models.BlackWhiteListDeviceDataModel, me._helper.coreLogService);
        break;
      case ContentType.Local_ValueStorage:
      case ContentType.Server_ValueStorage: // nutzt momentan den ValueStorage auch
        newManager = new ModelManager(models.ValueStorageDataModel, me._helper.coreLogService);
        break;
      case ContentType.Like:
        newManager = new ModelManager(models.LikeDataModel, me._helper.coreLogService);
        break;
      case ContentType.Rating:
        newManager = new ModelManager(models.RatingDataModel, me._helper.coreLogService);
        break;
      case ContentType.ExternalLinksSettings:
        newManager = new ModelManager(models.ExternalLinksSettingsDataModel, me._helper.coreLogService);
        break;
      case ContentType.ProductTag:
        newManager = new ModelManager(models.ArticleTagDataModel, me._helper.coreLogService);
        break;
      case ContentType.Inhouse_ServiceCalls:
        newManager = new ModelManager(models.ServiceCallDataModel, me._helper.coreLogService);
        break;
      case ContentType.OrderHistory:
        newManager = new ModelManager(models.OrderHistoryDataModel, me._helper.coreLogService);
        break;
      case ContentType.RecipeLinking:
        newManager = new ModelManager(models.RecipeLinkingDataModel, me._helper.coreLogService);
        break;
      case ContentType.Webvoucher_Custom:
        newManager = new ModelManager(models.WebvoucherDataModel, me._helper.coreLogService);
        break;
      case ContentType.Webvoucher_Layout_Preview:
        newManager = new ModelManager(models.WebvoucherLayoutDataModel, me._helper.coreLogService);
        break;
      // SOT
      case ContentType.SOTLanguages:
        newManager = new ModelManager(sotmodels.SOTLanguageDataModel, me._helper.coreLogService);
        break;
      case ContentType.SOTSettings:
        newManager = new ModelManager(sotmodels.SOTSettingsDataModel, me._helper.coreLogService);
        break;
      case ContentType.SOTStyle:
        newManager = new ModelManager(sotmodels.SOTStyleDataModel, me._helper.coreLogService);
        break;
      case ContentType.SOTItemGroup:
        newManager = new ModelManager(sotmodels.SOTItemGroupDataModel2, me._helper.coreLogService);
        break;
      case ContentType.SOTRecommendationRules:
        newManager = new ModelManager(sotmodels.SOTRecommendationRulesDataModel, me._helper.coreLogService);
        break;
      case ContentType.SOTStartpage:
        newManager = new ModelManager(sotmodels.SOTStartpageDataModel, me._helper.coreLogService);
        break;
      case ContentType.SOTAdvertisement:
        newManager = new ModelManager(sotmodels.SOTAdvertisementDataModel, me._helper.coreLogService);
        break;
      default:
        newManager = null;
        me._helper.coreLogService.error("ObjectObserver.CreateNewManager", "ContentType unknown", me._contentType);
        break;
    }
    return newManager;
  }

  /** Hilfsfunction um änderungen temporär zu speichern und bei internet wieder zu versenden. */
  private CreateServerCallHelperForSync(unsyncedData: T[]): ServerCallHelper {
    const me = this;
    const serverCall = new ServerCallHelper();
    switch (me._contentType) {
      case ContentType.Like:
        const SyncModel = new models.LikeSyncModel();
        // todo need be to test
        SyncModel.likes = new Array();
        for (const model of unsyncedData) {
          // todo warum klappt hier das casten nicht in <models.LikeDataModel>
          const likeModel: models.LikeDataModel = <any>model;
          const setLike = new models.SetLikeModel();
          setLike.content_type = likeModel.content_type;
          setLike.content_id = likeModel.content_id;
          setLike.like = !likeModel.deleted;
          SyncModel.likes.push(setLike);
        }
        serverCall.RequestData = SyncModel;
        serverCall.FunctionName = "like/SyncLikes";
        serverCall.SyncDataModelConstructor = models.LikeSyncDataModel;
        break;
      default:
        me._helper.coreLogService.error("ObjectObserver.CreateServerCallHelperForSync", "ContentType has no CallHelperForSync or is unknown", me._contentType);
        return null;
    }
    return serverCall;
  }

  private CreateServerCallHelper(): ServerCallHelper {
    const me = this;
    const serverCall = new ServerCallHelper();
    switch (me._contentType) {
      case ContentType.Location:
        serverCall.RequestData = new models.LocationSyncModel();
        serverCall.FunctionName = "location/GetNewLocations2";
        serverCall.SyncDataModelConstructor = models.LocationSyncDataModel;
        break;
      case ContentType.News:
        serverCall.RequestData = new models.NewsSyncModel();
        serverCall.FunctionName = "news/GetNewNews";
        serverCall.SyncDataModelConstructor = models.NewsSyncDataModel;
        break;
      case ContentType.Coupon:
        serverCall.RequestData = new models.CouponSyncModel();
        serverCall.FunctionName = "coupon/GetNewCoupons";
        serverCall.SyncDataModelConstructor = models.CouponSyncDataModel;
        break;
      case ContentType.Event:
        serverCall.RequestData = new models.EventSyncModel();
        serverCall.FunctionName = "event/GetNewEvents";
        serverCall.SyncDataModelConstructor = models.EventSyncDataModel;
        break;
      case ContentType.Impressum:
        serverCall.RequestData = new models.ImpressumSyncModel();
        serverCall.FunctionName = "impressum/GetNewImpressum";
        serverCall.SyncDataModelConstructor = models.ImpressumSyncDataModel;
        break;
      case ContentType.Term:
        serverCall.RequestData = new models.TermSyncModel();
        serverCall.FunctionName = "term/GetNewTerms";
        serverCall.SyncDataModelConstructor = models.TermSyncDataModel;
        break;
      case ContentType.Home_Slider:
        serverCall.RequestData = new models.HomeSliderSyncModel();
        serverCall.FunctionName = "homecontent/GetNewHomeSliders";
        serverCall.SyncDataModelConstructor = models.HomeSliderSyncDataModel;
        break;
      case ContentType.Home_Tile:
        serverCall.RequestData = new models.HomeTileSyncModel();
        serverCall.FunctionName = "homecontent/GetNewHomeTiles";
        serverCall.SyncDataModelConstructor = models.HomeTileSyncDataModel;
        break;
      case ContentType.Home_PrioTile:
        serverCall.RequestData = new models.HomePrioTileSyncModel();
        serverCall.FunctionName = "homecontent/GetNewHomePrioTiles";
        serverCall.SyncDataModelConstructor = models.HomePrioTileSyncDataModel;
        break;
      case ContentType.Navigation:
        serverCall.RequestData = new models.NavigationSyncModel();
        serverCall.FunctionName = "navigation/GetNewNavigation";
        serverCall.SyncDataModelConstructor = models.NavigationSyncDataModel;
        break;
      case ContentType.Item_Group:
        serverCall.RequestData = new models.ItemGroupSyncModel();
        serverCall.FunctionName = "menu/GetNewItemGroups";
        serverCall.SyncDataModelConstructor = models.ItemGroupSyncDataModel;
        break;
      case ContentType.BonusactionSettings:
        serverCall.RequestData = new models.BonusactionSettingsSyncModel();
        serverCall.FunctionName = "bonussystem/GetNewBonusactionSettings";
        serverCall.SyncDataModelConstructor = models.BonusactionSettingsSyncDataModel;
        break;
      case ContentType.Bonusaction:
        serverCall.RequestData = new models.BonusactionSyncModel();
        serverCall.FunctionName = "bonussystem/GetNewBonusactions";
        serverCall.SyncDataModelConstructor = models.BonusactionSyncDataModel;
        break;
      case ContentType.BonusactionBenefit:
        serverCall.RequestData = new models.BonusactionBenefitSyncModel();
        serverCall.FunctionName = "bonussystem/GetNewBonusactionBenefits";
        serverCall.SyncDataModelConstructor = models.BonusactionBenefitSyncDataModel;
        break;
      case ContentType.Settings:
        serverCall.RequestData = new models.SettingsSyncModel();
        serverCall.FunctionName = "settings/GetNewSettings";
        serverCall.SyncDataModelConstructor = models.SettingsSyncDataModel;
        break;
      case ContentType.MenuSettings:
        serverCall.RequestData = new models.MenuSettingsSyncModel();
        serverCall.FunctionName = "menu/GetNewMenuSettings2";
        serverCall.SyncDataModelConstructor = models.MenuSettingsSyncDataModel2;
        break;
      case ContentType.WebshopSettings:
        serverCall.RequestData = new models.WebshopSettingsSyncModel2();
        serverCall.FunctionName = "webshop/GetNewWebshopSettings2";
        serverCall.SyncDataModelConstructor = models.WebshopSettingsSyncDataModel2;
        break;
      case ContentType.WebshopColorTokens:
        // no hyp_cus_nr on the request, an app installation always belongs to exactly one customer (no location parameter, same as WebshopSettings)
        serverCall.RequestData = new models.WebshopColorTokensSyncModel();
        // implemented in YourAppServer, not in the Portal or uapp repo, analogous to "sot/GetNewStyle" below
        serverCall.FunctionName = "webshop/GetNewColorTokens";
        serverCall.SyncDataModelConstructor = models.WebshopColorTokensSyncDataModel;
        break;
      case ContentType.ReservationSettings:
        serverCall.RequestData = new models.ReservationSettingsSyncModel();
        serverCall.FunctionName = "reservation/GetNewReservationSettings";
        serverCall.SyncDataModelConstructor = models.ReservationSettingsSyncDataModel;
        break;
      case ContentType.sub_Product:
        if (Utils.IsNull(me._data)) {
          me._helper.coreLogService.error("ObjectObserver.CreateServerCallHelper", "ContentType Product hat keine ObjectObserverData");
          return null;
        }
        const RequestData_Products = new models.ProductsSyncModel4();
        RequestData_Products.hyp_cus_nr = me._data.intern_data["hyp_cus_nr"];
        RequestData_Products.sortiment = me._data.intern_data["sortiment"];
        serverCall.RequestData = RequestData_Products;
        serverCall.FunctionName = "menu/GetNewProducts4";
        serverCall.SyncDataModelConstructor = models.ProductsSyncDataModel4;
        break;
      case ContentType.sub_WebshopLanguages:
        if (Utils.IsNull(me._data)) {
          me._helper.coreLogService.error("ObjectObserver.CreateServerCallHelper", "ContentType WebshopLanguages hat keine ObjectObserverData");
          return null;
        }
        const RequestData_WebshopLanguages = new models.WebshopLanguagesAsPackageSyncModel();
        RequestData_WebshopLanguages.location = me._data.intern_data["location"];
        RequestData_WebshopLanguages.code = me._data.intern_data["code"];
        serverCall.RequestData = RequestData_WebshopLanguages;
        serverCall.FunctionName = "webshop/GetNewWebshopLanguageAsPackage";
        serverCall.SyncDataModelConstructor = models.WebshopLanguagesAsPackageSyncDataModel;
        break;
      case ContentType.ArticleAvailabililty:
        serverCall.RequestData = new models.ArticleAvailabilitySyncModel();
        serverCall.FunctionName = "webshop/GetNewArticleAvailabilities";
        serverCall.SyncDataModelConstructor = models.ArticleAvailabilitySyncDataModel;
        break;
      case ContentType.BlackWhiteList:
        serverCall.RequestData = new models.BlackWhiteListSyncModel();
        serverCall.FunctionName = "blackwhitelist/GetNewBlackWhiteListDevices";
        serverCall.SyncDataModelConstructor = models.BlackWhiteListSyncDataModel;
        break;
      case ContentType.Like:
        serverCall.RequestData = new models.LikeSyncModel();
        serverCall.FunctionName = "like/GetNewLikes";
        serverCall.SyncDataModelConstructor = models.LikeSyncDataModel;
        break;
      case ContentType.Rating:
        serverCall.RequestData = new models.RatingSyncModel();
        serverCall.FunctionName = "rating/GetNewRatings";
        serverCall.SyncDataModelConstructor = models.RatingSyncDataModel;
        break;
      case ContentType.ExternalLinksSettings:
        serverCall.RequestData = new models.ExternalLinksSettingsSyncModel();
        serverCall.FunctionName = "externalLinksSettings/GetNewExternalLinksSettings";
        serverCall.SyncDataModelConstructor = models.ExternalLinksSettingsSyncDataModel;
        break;
      case ContentType.ProductTag:
        serverCall.RequestData = new models.ArticleTagSyncModel();
        serverCall.FunctionName = "webshop/GetNewArticleTags";
        serverCall.SyncDataModelConstructor = models.ArticleTagSyncDataModel;
        break;
      case ContentType.Inhouse_ServiceCalls:
        serverCall.RequestData = new models.ServiceCallSyncModel();
        serverCall.FunctionName = "webshop/GetNewServiceCalls";
        serverCall.SyncDataModelConstructor = models.ServiceCallSyncDataModel;
        break;
      case ContentType.OrderHistory:
        serverCall.RequestData = new models.OrderHistorySyncModel();
        serverCall.FunctionName = "webshop/GetOrderHistory";
        serverCall.SyncDataModelConstructor = models.OrderHistorySyncDataModel;
        break;
      case ContentType.RecipeLinking:
        serverCall.RequestData = new models.RecipeLinkingsSyncModel();
        serverCall.FunctionName = "recipelinking/GetNewRecipeLinkings";
        serverCall.SyncDataModelConstructor = models.RecipeLinkingsSyncDataModel;
        break;
      case ContentType.Webvoucher_Custom:
        serverCall.RequestData = new models.WebvoucherSyncModel();
        serverCall.FunctionName = "webvoucher/GetNewWebvouchers";
        serverCall.SyncDataModelConstructor = models.WebvoucherSyncDataModel;
        break;
      case ContentType.Webvoucher_Layout_Preview:
        serverCall.RequestData = new models.WebvoucherLayoutSyncModel();
        serverCall.FunctionName = "webvoucher/GetNewWebvoucherLayouts";
        serverCall.SyncDataModelConstructor = models.WebvoucherLayoutSyncDataModel;
        break;
      // SOT
      case ContentType.SOTLanguages:
        const RequestData_SOTLanguage = new sotmodels.SOTLanguageSyncModel();
        RequestData_SOTLanguage.hyp_cus_nr = me._data.intern_data["hyp_cus_nr"];
        serverCall.RequestData = RequestData_SOTLanguage;
        serverCall.FunctionName = "sot/GetNewLanguages";
        serverCall.SyncDataModelConstructor = sotmodels.SOTLanguageSyncDataModel;
        break;
      case ContentType.SOTSettings:
        const RequestData_SOTSettings = new sotmodels.SOTSettingsSyncModel();
        RequestData_SOTSettings.hyp_cus_nr = me._data.intern_data["hyp_cus_nr"];
        serverCall.RequestData = RequestData_SOTSettings;
        serverCall.FunctionName = "sot/GetNewSettings";
        serverCall.SyncDataModelConstructor = sotmodels.SOTSettingsSyncDataModel;
        break;
      case ContentType.SOTStyle:
        const RequestData_SOTStyle = new sotmodels.SOTStyleSyncModel();
        RequestData_SOTStyle.hyp_cus_nr = me._data.intern_data["hyp_cus_nr"];
        serverCall.RequestData = RequestData_SOTStyle;
        serverCall.FunctionName = "sot/GetNewStyle";
        serverCall.SyncDataModelConstructor = sotmodels.SOTStyleSyncDataModel;
        break;
      case ContentType.SOTItemGroup:
        const RequestData_SOTItemGroup = new sotmodels.SOTItemGroupSyncModel();
        RequestData_SOTItemGroup.hyp_cus_nr = me._data.intern_data["hyp_cus_nr"];
        serverCall.RequestData = RequestData_SOTItemGroup;
        serverCall.FunctionName = "sot/GetNewItemGroups2";
        serverCall.SyncDataModelConstructor = sotmodels.SOTItemGroupSyncDataModel2;
        break;
      case ContentType.SOTRecommendationRules:
        const RequestData_SOTRecommendationRules = new sotmodels.SOTRecommendationRulesSyncModel();
        RequestData_SOTRecommendationRules.hyp_cus_nr = me._data.intern_data["hyp_cus_nr"];
        serverCall.RequestData = RequestData_SOTRecommendationRules;
        serverCall.FunctionName = "sot/GetNewRecommendationRules";
        serverCall.SyncDataModelConstructor = sotmodels.SOTRecommendationRulesSyncDataModel;
        break;
      case ContentType.SOTStartpage:
        const RequestData_SOTStartpage = new sotmodels.SOTStartpageSyncModel();
        RequestData_SOTStartpage.hyp_cus_nr = me._data.intern_data["hyp_cus_nr"];
        serverCall.RequestData = RequestData_SOTStartpage;
        serverCall.FunctionName = "sot/GetNewStartpages";
        serverCall.SyncDataModelConstructor = sotmodels.SOTStartpageSyncDataModel;
        break;
      case ContentType.SOTAdvertisement:
        const RequestData_SOTAdvertisement = new sotmodels.SOTAdvertisementSyncModel();
        RequestData_SOTAdvertisement.hyp_cus_nr = me._data.intern_data["hyp_cus_nr"];
        serverCall.RequestData = RequestData_SOTAdvertisement;
        serverCall.FunctionName = "sot/GetNewAdvertisement";
        serverCall.SyncDataModelConstructor = sotmodels.SOTAdvertisementSyncDataModel;
        break;
      // storage
      case ContentType.Local_ValueStorage:
        me._helper.coreLogService.error("ObjectObserver.CreateServerCallHelper", "ContentType Local_ValueStorage wird nicht vom server geholt");
        return null;
      case ContentType.Server_ValueStorage:
        me._helper.coreLogService.error("ObjectObserver.CreateServerCallHelper", "ContentType Server_ValueStorage not implemented yet");
        return null;
      default:
        me._helper.coreLogService.error("ObjectObserver.CreateServerCallHelper", "ContentType unknown", me._contentType);
        return null;
    }
    return serverCall;
  }

  /** Gets the Manager synclocked. This is importent to reduce multible loading calls, by getting data from server. */
  private SafeGetManager(refreshAfterInit: boolean, waitForServerIfLocal: boolean = false): Promise<void> {
    const me = this;
    return me._SafeGetManagerMutex.use<void>(async () => {
      await me._GetManager(refreshAfterInit, waitForServerIfLocal);
    });
  }

  /** Release the Manager for free more ram, its creates again if its need. */
  public Release(): Promise<void> {
    const me = this;
    return me._SafeGetManagerMutex.use<void>(() => {
      me._Release();
    });
  }

  /** Release the Manager for free more ram, its creates again if its need. */
  public _Release(): void {
    const me = this;
    me._manager = null;
  }

  /** Deletes the local storaged data and free the ram by release.
   * @param force deletes if delete is default not allowed
  */
  public DeleteLocalStoredData(force: boolean = false): Promise<void> {
    const me = this;
    if (ContentTypeGroups.deleteLocalStoragedDataAllowedContentTypes(environment.producttype).indexOf(me.ContentType) < 0) {
      if (force) {
        me._helper.coreLogService.info("ObjectObserver.DeleteLocalStoredData", "forced to delete", environment.producttype, me.ContentType);
      } else {
        return Promise.reject("DeleteLocalStoredData is not allowed for ContentType: " + me.ContentType);
      }
    }

    return me._SafeGetManagerMutex.use<void>(async () => {
      await me._DeleteLocalStoregdData();
      me._Release();
    });
  }

  /** (Attention, must run in Mutex) Gets the manager, or loading it from storage and refresh data from server.
  * @param refreshAfterInit nach dem lesen aus dem Storage wird der Server auch angefragt, Wichtig, man muss das Changeevent behandeln
  * @param waitForServerIfLocal wenn die Daten lokal vorliegen und der Server abgefragt werden soll, so werden die lokalen Daten nicht sofort zurückgegeben, sondern auf sie Server Antwort gewartet
  */
  private async _GetManager(refreshAfterInit: boolean, waitForServerIfLocal: boolean = false): Promise<void> {
    const me = this;
    // SOT braucht immer nur die vom server bereitgestellten daten, da es initial bereits alle Daten ermittelt
    if (environment.producttype == YourAPPProductType.sot) {
      waitForServerIfLocal = true;
    }

    if (me._manager !== null) {
      return; // manager already loaded
    } else {
      try {
        const storedManager = await me._ReadManagerFromStorage()
        // ReadManager liefer null, wenn der Manager nicht in im Storage vorliegt
        // in diesem Fall versucht man die Daten vom Server zu laden,
        // ansonsten wird nur der versuchgemacht die daten zu aktualisieren
        if (!Utils.IsNull(storedManager)) {
          me._manager = storedManager;
          if (refreshAfterInit) {
            if (waitForServerIfLocal) {
              await me._RefreshObjectsAndSyncWithServer();
            } else {
              me._RefreshObjectsAndSyncWithServer().then(/* do it async */).catch(/* ignore */);
            }
          }
          return; // manager loaded
        } else {
          // _manager leer füllen
          me._manager = me.CreateNewManager();
          if (me.SynchronizingWithServer) {
            try {
              const serverData = await me._GetObjectsFromServer(me._manager);
              me._manager.extendWithSyncData(serverData);
              try {
                await me._SaveManagerToStorage();
                // todo her can sync unsynced, or in _SaveManagerToStorage?
                const keys: (number | string)[] = new Array();
                if (serverData && serverData.models) {
                  serverData.models.forEach(model => {
                    keys.push(model.DM_Key);
                  });
                  me._CompleteObjectsFromServer(keys).then(/* do it async */).catch(/* ignore */);
                } else {
                  me._helper.coreLogService.error("ObjectObserver._GetManager", "ServerData or ServerData.models is null", me._contentType);
                }
                if (keys.length > 0) {
                  // notify about changed keys after return, provent endless loops
                  setTimeout(() => { me._observerChanged.next(keys); }, 0);
                }
                return;
              } catch (error) {
                me._helper.coreLogService.error("ObjectObserver._GetManager", "_SaveManagerToStorage", error);
                return; // return and no Error because it is a try to get data from server
              }
            } catch (error) {
              me._helper.coreLogService.error("ObjectObserver._GetManager", "_GetObjectsFromServer (mobile)", error);
              return; // return and no Error because it is a try to get data from server
            }
          } else {
            return; // manager loaded
          }
        }
      } catch (error) {
        me._helper.coreLogService.error("ObjectObserver._GetManager", "_ReadObjectsFromStorage", error);
        throw new Error();
      }
    }
  }

  /** Fragt die Daten vom Server und synchronisiert diese anschliessend (synchronisation ist async) */
  private async _RefreshObjectsAndSyncWithServer(): Promise<void> {
    const me = this;
    if (me.SynchronizingWithServer && me._helper.coreCOMService.environment.networkInformation.isOnline) {
      // versuche die Daten daraufhin (nach resolve) zu aktualisieren
      // -> nach resolve, damit der anfragende Service die angeforderten daten vorliegen hat;
      try {
        await me._RefreshObjectsFromServer();
        me.SynchronizeObjectsWithServer().then(/* do it async */).catch(/* ignore */);
      } catch (error) {
        me._helper.coreLogService.error("ObjectObserver._RefreshObjectsAndSyncWithServer", "_RefreshObjectsFromServer", error);
      }
    }
    return;
  }

  /**
   * Ermittlung von Abhängigkeiten, der vom Server heruntergeladener Daten
   * @param _manager ModelManager
   * @param keys Keys der neuen Daten
   */
  private _CompleteObjectsFromServer(keys: (string | number)[]): Promise<void> {
    const me = this;
    return new Promise<void>((resolve, reject) => {
      me._manager.postLoadData(keys, me._helper).then(() => {
        me._SaveManagerToStorage()
          .then(resolve)
          .catch((error) => {
            me._helper.coreLogService.error("ObjectObserver._CompleteObjectsFromServer", "_SaveManagerToStorage", error);
            reject();
          });
      }).catch((error) => {
        me._helper.coreLogService.error("ObjectObserver._CompleteObjectsFromServer", "_manager.postLoadData", error);
        reject();
      });
    });
  }

  /** gets the LastSyncAt with server */
  public GetLastSyncAt(): Promise<Moment> {
    const me = this;
    return new Promise<Moment>((resolve, reject) => {
      me.SafeGetManager(true)
        .then(() => {
          resolve(me._manager.LastSyncAt);
        })
        .catch((error) => {
          me._helper.coreLogService.error("ObjectObserver.GetLastSyncAt", "SafeGetManager", error);
          reject();
        });
    });
  }

  /** Contains, check if object exists with matched key */
  public Contains(key: number | string): Promise<boolean> {
    const me = this;
    return new Promise<boolean>((resolve, reject) => {
      me.SafeGetManager(true)
        .then(() => {
          resolve(me._manager.contains(key));
        })
        .catch((error) => {
          me._helper.coreLogService.error("ObjectObserver.Contains", "SafeGetManager", error);
          reject();
        });
    });
  }

  /** ContainsByFilter, check if exists an abject with filter matched  */
  public ContainsByFilter(filter: (obj: T) => boolean): Promise<boolean> {
    const me = this;
    return new Promise<boolean>((resolve, reject) => {
      me.SafeGetManager(true)
        .then(() => {
          resolve(me._manager.containsByFilter(filter));
        })
        .catch((error) => {
          me._helper.coreLogService.error("ObjectObserver.GetObjectsByFilter", "SafeGetManager", error);
          reject();
        });
    });
  }

  /** GetObject */
  public GetObject(key: number | string, waitForServerIfLocal: boolean = false): Promise<T> {
    const me = this;
    return new Promise<T>((resolve, reject) => {
      me.SafeGetManager(true, waitForServerIfLocal)
        .then(() => {
          resolve(me._manager.get(key));
        })
        .catch((error) => { me._helper.coreLogService.error("ObjectObserver.GetObject", "SafeGetManager", error); reject(); });
    });
  }

  /** GetObjectsByFilter */
  public GetObjectsByFilter(filter: (obj: T) => boolean, waitForServerIfLocal: boolean = false): Promise<T[]> {
    const me = this;
    return new Promise<T[]>((resolve, reject) => {
      me.SafeGetManager(true, waitForServerIfLocal)
        .then(() => {
          resolve(me._manager.getByFilter(filter));
        })
        .catch((error) => { me._helper.coreLogService.error("ObjectObserver.GetObjectsByFilter", "SafeGetManager", error); reject(); });
    });
  }

  /** GetAllObjects */
  public GetAllObjects(waitForServerIfLocal: boolean = false): Promise<T[]> {
    const me = this;
    return new Promise<T[]>((resolve, reject) => {
      me.SafeGetManager(true, waitForServerIfLocal)
        .then(() => {
          resolve(me._manager.getAll());
        })
        .catch((error) => { me._helper.coreLogService.error("ObjectObserver.GetAllObjects", "SafeGetManager", error); reject(); });
    });
  }

  /** SetObject */
  public SetObject(model: T): Promise<void> {
    const me = this;
    return new Promise<void>((resolve, reject) => {
      me.SafeGetManager(true)
        .then(() => {
          me._manager.set(model);
          me._SaveManagerToStorage()
            .then(() => {
              // todo her can sync unsynced, or in _SaveManagerToStorage?
              const keys: (number | string)[] = new Array();
              keys.push(model.DM_Key);
              resolve(); // first resolve and then the _observerChanged, provent endless loops
              me._observerChanged.next(keys);
            })
            .catch((error) => { me._helper.coreLogService.error("ObjectObserver.SetObject", "_SaveManagerToStorage", error); reject(); });
        })
        .catch((error) => { me._helper.coreLogService.error("ObjectObserver.SetObject", "SafeGetManager", error); reject(); });
    });
  }

  /** SetObjects */
  public SetObjects(models: T[]): Promise<void> {
    const me = this;
    return new Promise<void>((resolve, reject) => {
      me.SafeGetManager(true)
        .then(() => {
          models.forEach(model => {
            me._manager.set(model);
          });
          me._SaveManagerToStorage()
            .then(() => {
              // todo her can sync unsynced, or in _SaveManagerToStorage?
              const keys: (number | string)[] = new Array();
              models.forEach(model => {
                keys.push(model.DM_Key);
              });
              resolve(); // first resolve and then the _observerChanged, provent endless loops
              if (keys.length > 0) {
                me._observerChanged.next(keys); // notify about changed keys
              }
            })
            .catch((error) => { me._helper.coreLogService.error("ObjectObserver.SetObjects", "_SaveManagerToStorage", error); reject(); });
        })
        .catch((error) => { me._helper.coreLogService.error("ObjectObserver.SetObjects", "SafeGetManager", error); reject(); });
    });
  }

  /** SetObjectIntern */
  public SetObjectIntern(model: T, suppressChangeEvent: boolean, key: number): Promise<void> {
    const me = this;
    return new Promise<void>((resolve, reject) => {
      me.SafeGetManager(true)
        .then(() => {
          me._manager.setIntern(model, key);
          me._SaveManagerToStorage()
            .then(() => {
              if (!suppressChangeEvent) {
                const keys: (number | string)[] = new Array();
                keys.push(model.DM_Key);
                resolve(); // first resolve and then the _observerChanged, provent endless loops
                me._observerChanged.next(keys);
              } else {
                resolve();
              }
            })
            .catch((error) => { me._helper.coreLogService.error("ObjectObserver.SetObjectIntern", "_SaveManagerToStorage", error); reject(); });
        })
        .catch((error) => { me._helper.coreLogService.error("ObjectObserver.SetObjectIntern", "SafeGetManager", error); reject(); });
    });
  }

  /** SetObjectsIntern */
  public SetObjectsIntern(models: T[], suppressChangeEvent: boolean, key: number): Promise<void> {
    const me = this;
    return new Promise<void>((resolve, reject) => {
      me.SafeGetManager(true)
        .then(() => {
          models.forEach(model => {
            me._manager.setIntern(model, key);
          });
          me._SaveManagerToStorage()
            .then(() => {
              if (!suppressChangeEvent) {
                const keys: (number | string)[] = new Array();
                models.forEach(model => {
                  keys.push(model.DM_Key);
                });
                resolve(); // first resolve and then the _observerChanged, provent endless loops
                if (keys.length > 0) {
                  me._observerChanged.next(keys); // notify about changed keys
                }
              } else {
                resolve();
              }
            })
            .catch((error) => { me._helper.coreLogService.error("ObjectObserver.SetObjectsIntern", "_SaveManagerToStorage", error); reject(); });
        })
        .catch((error) => { me._helper.coreLogService.error("ObjectObserver.SetObjectsIntern", "SafeGetManager", error); reject(); });
    });
  }

  /** Notifyer of chnges in the Data, ist returns the DMKeys of the changed Objects */
  public get ObserveChanges(): Observable<(number | string)[]> {
    return this._observerChanged;
  }

  /** ableich der gespeicherten Daten mit dem Server */
  public SynchronizeObjectsWithServer(): Promise<SyncDataModel> {
    const me = this;
    return new Promise<SyncDataModel>((resolve, reject) => {
      me.SafeGetManager(false)
        .then(() => {
          if (!me.SynchronizingWithServer || Utils.IsNull(me._manager) || !me._manager.hasUnsynced()) {
            return resolve(null);
          }
          // allocate the model type for load it from server, and check internet connection
          const serverCall = me.CreateServerCallHelperForSync(me._manager.getUnsynced());
          if (Utils.IsNull(serverCall)) {
            return reject("ServerCall can not create");
          } else if (!me._helper.coreCOMService.environment.networkInformation.isOnline) {
            return reject("no internet");
          }

          me._helper.yourAppServer.Api.RequestService(serverCall.SyncDataModelConstructor, serverCall.FunctionName, serverCall.RequestData)
            .then((response) => {
              if (response.OK) {
                resolve(response.Data);
              } else {
                me._helper.coreLogService.error("ObjectObserver.SynchronizeObjectsWithServer", "RequestService (response not OK)", response);
                reject(response.HttpResponse.codeText);
              }
            })
            .catch((error) => { me._helper.coreLogService.error("ObjectObserver.SynchronizeObjectsWithServer", "RequestService (error)", error); reject(); });
        })
        .catch((error) => { me._helper.coreLogService.error("ObjectObserver.SynchronizeObjectsWithServer", "SafeGetManager", error); reject(); });
    });
  }

  public async RefreshObjectsFromServer(): Promise<void> {
    const me = this;
    if (!me._helper.coreCOMService.environment.networkInformation.isOnline) {
      throw new Error("no internet");
    }

    try {
      await me.SafeGetManager(false);
      return me._RefreshObjectsFromServer();
    } catch (error) {
      me._helper.coreLogService.error("ObjectObserver.RefreshObjectsFromServer", "SafeGetManager", error);
      throw new Error();
    }
  }

  private async _RefreshObjectsFromServer(): Promise<void> {
    const me = this;
    if (!me._helper.coreCOMService.environment.networkInformation.isOnline) {
      throw new Error("no internet");
    }

    try {
      if (Utils.IsNull(me._manager)) {
        throw new Error("manager is null");
      }
      if (!me.SynchronizingWithServer) {
        return; // no sync with server need
      }
      try {
        const serverData = await me._GetObjectsFromServer(me._manager);
        me._manager.extendWithSyncData(serverData);
        try {
          await me._SaveManagerToStorage();
          // todo her can sync unsynced, or in _SaveManagerToStorage?
          const keys: (number | string)[] = new Array();
          if (serverData && serverData.models) {
            serverData.models.forEach(model => {
              keys.push(model.DM_Key);
            });
            try {
              await me._CompleteObjectsFromServer(keys);
            } catch (error) {
              me._helper.coreLogService.error("ObjectObserver._RefreshObjectsFromServer", "_CompleteObjectsFromServer", error);
            }
          } else {
            me._helper.coreLogService.error("ObjectObserver._RefreshObjectsFromServer", "ServerData or ServerData.models is null", me._contentType);
          }
          if (keys.length > 0) {
            // notify about changed keys after return, provent endless loops
            setTimeout(() => { me._observerChanged.next(keys); }, 0);
          }
          return;
        } catch (error) {
          me._helper.coreLogService.error("ObjectObserver._RefreshObjectsFromServer", "_SaveManagerToStorage", error);
          throw new Error();
        }
      } catch (error) {
        me._helper.coreLogService.error("ObjectObserver._RefreshObjectsFromServer", "_GetObjectsFromServer (mobile)", error);
        throw new Error();
      }
    } catch (error) {
      me._helper.coreLogService.error("ObjectObserver._RefreshObjectsFromServer", "SafeGetManager", error);
      throw new Error();
    }
  }

  private _GetObjectsFromServer(manager: ModelManager<T>): Promise<SyncDataModel> {
    const me = this;
    if (!me.SynchronizingWithServer) {
      return Promise.resolve(null);
    }
    return new Promise<SyncDataModel>((resolve, reject) => {
      // allocate the model type for load it from server
      const serverCall = me.CreateServerCallHelper();
      if (Utils.IsNull(serverCall)) {
        return reject("ServerCall can not create");
      }

      // wenn Daten vorliegen, dann nur aktuallisieren
      if (!Utils.IsNull(manager) && manager.LastSyncAt > serverCall.RequestData.changes_since) {
        serverCall.RequestData.changes_since_ymd = manager.LastSyncAt_ymd;
        serverCall.RequestData.changes_since_hms = manager.LastSyncAt_hms;
      }

      me._helper.yourAppServer.Api.RequestService(serverCall.SyncDataModelConstructor, serverCall.FunctionName, serverCall.RequestData)
        .then((response) => {
          if (response.OK) {
            // me._helper.coreLogService.debug("ObjectObserver._GetObjectsFromServer", "RequestService (response OK)", response);
            resolve(response.Data);
          } else {
            me._helper.coreLogService.error("ObjectObserver._GetObjectsFromServer", "RequestService (response not OK)", [response, serverCall]);
            reject(response.HttpResponse.codeText);
          }
        })
        .catch((error) => { me._helper.coreLogService.error("ObjectObserver._GetObjectsFromServer", "RequestService (error)", error); reject(); });
    });
  }

  private _ReadManagerFromStorage(): Promise<ModelManager<T>> {
    const me = this;
    return new Promise<ModelManager<T>>((resolve, reject) => {
      me._ReadManagerFromTypedStorage(EnvDir.Data)
        .then((dataStoreManager) => {
          if (!Utils.IsNull(dataStoreManager)) {
            resolve(dataStoreManager);
          } else {
            me._ReadManagerFromTypedStorage(EnvDir.Assets)
              .then((assetsStoreManager) => {
                if (!Utils.IsNull(assetsStoreManager)) {
                  resolve(assetsStoreManager);
                } else {
                  // file dose not exists, return null for special case to get initial data
                  resolve(null);
                }
              })
              .catch((error) => { me._helper.coreLogService.error("ObjectObserver._ReadManagerFromStorage", "_ReadManagerFromTypedStorage (assets)", error); reject(); });
          }
        })
        .catch((error) => { me._helper.coreLogService.error("ObjectObserver._ReadManagerFromStorage", "_ReadManagerFromTypedStorage (data)", error); reject(); });
    });
  }

  private _ReadManagerFromTypedStorage(dir: EnvDir): Promise<ModelManager<T>> {
    const me = this;
    return new Promise<ModelManager<T>>((resolve, reject) => {
      // if data not set initial as empty
      if (me._helper.coreCOMService.environment.client.isMobile) {
        me._helper.safeFilesystem.getFile(dir, "store", me.StorageName + ".json")
          .then((fileEntry: FileEntry) => {
            if (Utils.IsNull(fileEntry)) { // not found
              resolve(null); // datei ist nicht vorhanden = datei ist leer
            } else {
              me._helper.safeFilesystem.readStringFromFileByEntry(fileEntry)
                .then((data) => {
                  if (data.length > 0) {
                    const loadedManager = me.CreateNewManager();
                    loadedManager.deserialize(data);
                    resolve(loadedManager);
                  } else {
                    resolve(null); // datei ist leer
                  }
                })
                .catch((error) => { me._helper.coreLogService.error("ObjectObserver._ReadManagerFromTypedStorage", "readStringFromFileByEntry", error); reject(); });
            }
          })
          .catch((error) => { me._helper.coreLogService.error("ObjectObserver._ReadManagerFromTypedStorage", "getFile", error); reject(); });
      } else {
        // Browser hold the changes in browser database IndexedDB
        me._helper.coreCOMService.environment.indexedDB.get(me.StorageName).then((data) => {
          if (data && data.length > 0) {
            const loadedManager = me.CreateNewManager();
            loadedManager.deserialize(data);
            resolve(loadedManager);
          } else {
            resolve(null); // data ist leer order nicht vorhanden
          }
        })
          .catch((error) => { me._helper.coreLogService.error("ObjectObserver._ReadManagerFromTypedStorage", "indexedDB.get", error); reject(); });
      }
    });
  }

  /** löscht die lokalen Daten, wenn sie in dem beschreibaren Speicher liegt */
  private _DeleteLocalStoregdData(): Promise<void> {
    const me = this;
    return new Promise<void>((resolve, reject) => {
      // if data not set initial as empty
      if (me._helper.coreCOMService.environment.client.isMobile) {
        me._helper.safeFilesystem.getFile(EnvDir.Data, "store", me.StorageName + ".json")
          .then((fileEntry: FileEntry) => {
            if (Utils.IsNull(fileEntry)) { // not found
              resolve(); // datei ist nicht vorhanden = datei ist leer
            } else {
              me._helper.safeFilesystem.removeFileByEntry(fileEntry)
                .then((removed) => {
                  if (removed) {
                    resolve();
                  } else {
                    me._helper.coreLogService.error("ObjectObserver._DeleteLocalStoregdData", "removeFileByEntry(false)", fileEntry);
                    reject("File '" + fileEntry.fullPath + "' can not be removed");
                  }
                })
                .catch((error) => { me._helper.coreLogService.error("ObjectObserver._DeleteLocalStoregdData", "removeFileByEntry", fileEntry, error); reject(); });
            }
          })
          .catch((error) => { me._helper.coreLogService.error("ObjectObserver._DeleteLocalStoregdData", "getFile", error); reject(); });
      } else {
        // Browser hold the changes in browser database IndexedDB
        me._helper.coreCOMService.environment.indexedDB.delete(me.StorageName).then(() => {
          resolve();
        })
          .catch((error) => { me._helper.coreLogService.error("ObjectObserver._DeleteLocalStoregdData", "indexedDB.delete", error); reject(); });
      }
    });
  }

  private _SaveManagerToStorage(): Promise<void> {
    const me = this;
    return new Promise<void>((resolve, reject) => {
      if (me.needSave()) {
        if (me._helper.coreCOMService.environment.client.isMobile) {
          me._helper.safeFilesystem.createFile(EnvDir.Data, "store", me.StorageName + ".json")
            .then((fileEntry: FileEntry) => {
              me._helper.safeFilesystem.writeStringToFileByEntry(fileEntry, me._manager.serialize(), false)
                .then(() => {
                  me.markSaved();
                  resolve();
                })
                .catch((error) => { me._helper.coreLogService.error("ObjectObserver._SaveManagerToStorage", "writeStringToFileByEntry", error); reject(); });
            })
            .catch((error) => { me._helper.coreLogService.error("ObjectObserver._SaveManagerToStorage", "createFile", error); reject(); });
        } else {
          // Browser hold the changes in browser database IndexedDB
          me._helper.coreCOMService.environment.indexedDB.put(me.StorageName, me._manager.serialize()).then(() => {
            me.markSaved();
            resolve();
          })
            .catch((error) => { me._helper.coreLogService.error("ObjectObserver._SaveManagerToStorage", "indexedDB.put", error); reject(); });
        }
      } else {
        // save dosnt need
        resolve();
      }
    });
  }
}
